import React from "react";

import { Button } from "../../../components/ui/button";
import { cn } from "../../../utils/cn";

/** Hover/idle color curve per tone. Muted at rest in every case; the hover
 *  target is what distinguishes a destructive remove (→ error) from a neutral
 *  toggle (→ base). Both keep the 150ms `transition-colors` the raw buttons
 *  used. The `active:` / `aria-selected:` overrides pin the pressed/selected
 *  color back to the idle target so the shared `Button transparent` variant's
 *  baked ember active-curve doesn't flash. */
const TONE_CLASS = {
    destructive:
        "text-foreground-muted hover:text-error-base active:text-error-base aria-selected:text-foreground-muted transition-colors duration-150",
    neutral:
        "text-foreground-muted hover:text-foreground-base active:text-foreground-base aria-selected:text-foreground-muted transition-colors duration-150",
    // muted → ember: the transparent variant's own hover, for text affordances
    // like "Edit" that should read as the accent action on hover.
    ember: "text-foreground-muted hover:text-ember active:text-ember aria-selected:text-foreground-muted transition-colors duration-150"
} as const;

/** Forced hover/press tone (sim): literal color classes matching each tone's
 *  `hover:` / `active:` curve, so a scripted cursor can paint the state with
 *  no pointer. CSS pseudo-states can't be JS-driven; these unconditional
 *  utilities trail `TONE_CLASS` so tailwind-merge resolves in their favor. */
const FORCED_TONE_CLASS = {
    destructive: "text-error-base",
    neutral: "text-foreground-base",
    ember: "text-ember"
} as const;

export interface IconButtonProps {
    /** The icon (or short text) rendered inside the button. */
    children: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** Color curve: `destructive` = muted → error (Remove / ✕), `neutral` =
     *  muted → foreground-base, `ember` = muted → ember (the accent hover, e.g.
     *  the "Edit" affordance). Default `destructive`. */
    tone?: keyof typeof TONE_CLASS;
    /** Button interaction style, forwarded to the inner `Button`. `underline`
     *  gives the transparent+underline affordance (always underlined, hover
     *  lifts the offset) for text actions like "Edit". Default `default`. */
    interaction?: "default" | "underline";
    /** Accessible label — required when `children` is icon-only. */
    "aria-label"?: string;
    className?: string;
    disabled?: boolean;
    /** Demo affordance — force the hover VISUAL (tone hover color) on with no
     *  pointer present, for scripted sims. Forwarded to the inner `Button`'s
     *  `forceHover` too. Default `false` — existing callers unaffected. */
    forceHover?: boolean;
    /** Demo affordance — force the pressed/active VISUAL on. Forwarded to the
     *  inner `Button`'s `forcePressed`. Default `false`. */
    forcePressed?: boolean;
    /** Sim-only cursor target key, forwarded to the inner `Button` so
     *  `useSimCursor().moveTo(key)` can travel here. Inert in production. */
    "data-sim-cursor-key"?: string;
}

/**
 * IconButton — the small, bare, transparent row affordance used for icon/text
 * "remove", "edit", and "cancel" actions across the Programs page. A Generic
 * Component wrapping the shared `Button` (`variant="transparent"`,
 * `interaction="default"`, `size="fit"`) with the sanctioned muted → error /
 * muted → base hover curve.
 */
export function IconButton({
    children,
    onClick,
    tone = "destructive",
    "aria-label": ariaLabel,
    className,
    disabled,
    forceHover = false,
    forcePressed = false,
    interaction = "default",
    "data-sim-cursor-key": simCursorKey
}: IconButtonProps): React.ReactElement {
    return (
        <Button
            variant="transparent"
            interaction={interaction}
            size="fit"
            onClick={onClick}
            aria-label={ariaLabel}
            disabled={disabled}
            forceHover={forceHover}
            forcePressed={forcePressed}
            data-sim-cursor-key={simCursorKey}
            className={cn(
                "shrink-0",
                TONE_CLASS[tone],
                className,
                // Forced hover/press tone last so it wins the color cascade.
                (forceHover || forcePressed) && FORCED_TONE_CLASS[tone]
            )}
        >
            {children}
        </Button>
    );
}
